'use client'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Input } from 'antd'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { NavSearchIcon } from '../svg/NavSearchIcon'
import { NavSearchDeleteIcon } from 'Icons'
import SuggestTags from './SuggestTags'
import RecentSearches from './RecentSearches'
import PopularTags from './PopularTags'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getPlatform } from '@/utils/platform'

// Add prop type for onOpenChange
interface SearchProps {
  onOpenChange?: (open: boolean) => void
}

// PC 搜索入口：下拉框（未输入时展示 Recent/Popular，输入时展示建议词），
// 点击搜索 / 选中建议词后跳转独立搜索结果落地页 /search。
// 设计稿：在 /search 结果页，搜索框保持展开并显示当前搜索词（不收起）。
export default function Search({ onOpenChange }: SearchProps) {
  const { t, lang } = useI18n()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // dropdownOpen 只控制下拉面板；输入框展开态 = dropdownOpen || 处于结果页 || 正在跳转结果页
  const [dropdownOpen, setDropdownOpen] = useState(false)
  // 跳转结果页的过渡期标记：避免 router.push 到 pathname 更新之间输入框先收起再展开的抖动
  const [navigatingToSearch, setNavigatingToSearch] = useState(false)
  const [text, setText] = useState('')
  const { trackEvent } = useQuickTracking()
  const platform = getPlatform()

  const isOnSearchPage = useMemo(
    () => pathname === '/search' || pathname?.endsWith('/search'),
    [pathname]
  )
  const expanded = dropdownOpen || isOnSearchPage || navigatingToSearch

  // 路由落地后清除过渡期标记（无论最终落在哪，都交还给 isOnSearchPage 接管）
  useEffect(() => {
    if (navigatingToSearch) {
      setNavigatingToSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  // 结果页：输入框回显当前搜索词（URL ?q=）；跳转过渡期不清空，避免文字闪烁
  useEffect(() => {
    if (isOnSearchPage) {
      setText(searchParams.get('q') || '')
    } else if (!navigatingToSearch) {
      setText('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnSearchPage, searchParams])

  // 输入框展开态变化时通知 Header 调整布局（隐藏左侧 Discord/Download 等）
  useEffect(() => {
    onOpenChange?.(expanded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  const handleOpen = () => {
    if (!dropdownOpen) {
      trackEvent('search_click_search_box', { platform }, 'CLK')
    }
    setDropdownOpen(true)
  }

  const handleClose = () => {
    setDropdownOpen(false)
    onOpenChange?.(false)
  }

  const handleCloseDropdown = () => {
    setDropdownOpen(false)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value)
    // 输入时展示建议词下拉
    if (!dropdownOpen) setDropdownOpen(true)
  }

  const startSearch = (rawQuery: string, nextSearchType: string) => {
    const query = rawQuery.trim()
    if (!query) return
    // 收起下拉面板；标记跳转过渡期，输入框保持展开不抖动
    setDropdownOpen(false)
    setNavigatingToSearch(true)
    setText(query)
    router.push(
      withLocale(
        `/search?q=${encodeURIComponent(query)}&type=${nextSearchType}`,
        lang
      )
    )
  }

  return (
    <>
      {dropdownOpen && (
        <div
          className="pointer-events-auto fixed inset-0 h-screen"
          onClick={handleCloseDropdown}
        />
      )}
      <div
        className={clsx(
          'relative ml-3 flex flex-1 justify-end',
          // 设计稿：展开时搜索框占满 Logo 与右侧按钮组之间的全部空间；收起时只是图标
          !expanded && 'max-w-[600px]'
        )}
      >
        <div
          className={clsx(
            'flex h-9 min-w-[36px] cursor-pointer justify-end rounded-full border border-secondary-border bg-secondary-bg transition-[flex] duration-300',
            expanded ? 'flex-1 border-white' : 'hover:border-hover-border'
          )}
          onClick={handleOpen}
        >
          <div
            className={clsx(
              'pointer-events-auto flex h-full items-center justify-center text-2xl',
              expanded ? 'ml-3 text-secondary-text' : 'w-9'
            )}
          >
            <NavSearchIcon open={expanded} />
          </div>
          {expanded && (
            <motion.div
              layout="size"
              initial={{ width: 0 }}
              animate={{ width: 'auto', flex: 1 }}
              className="pointer-events-auto"
            >
              <Input
                className="placeholder-input:text-634c54 h-[34px] flex-1 pl-1.5 text-sm !text-white"
                variant="borderless"
                autoFocus={!isOnSearchPage} // eslint-disable-line jsx-a11y/no-autofocus
                placeholder={t('Search characters or creators...')}
                value={text}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && text?.trim()) {
                    startSearch(text, 'search')
                  }
                }}
                allowClear={{
                  clearIcon: (
                    <motion.div
                      className="flex h-full items-center text-gray-300"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setText('')}
                    >
                      <NavSearchDeleteIcon />
                    </motion.div>
                  ),
                }}
              />
            </motion.div>
          )}
          {dropdownOpen && !text && (
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              aria-label={t('Close')}
              className="pointer-events-auto flex h-full w-9 flex-none items-center justify-center"
              onClick={(e) => {
                e.stopPropagation()
                handleClose()
              }}
            >
              <NavSearchDeleteIcon />
            </motion.button>
          )}
        </div>

        {dropdownOpen && (
          <div
            className="pointer-events-auto absolute left-0 right-0 top-12 rounded-[10px] border border-secondary-border bg-secondary-bg"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(87,47,102,0.8) 50%, rgba(29,21,21,0.8) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              willChange: 'backdrop-filter',
              transform: 'translateZ(0)',
              overflow: 'hidden',
            }}
          >
            <div
              id="scrollableDiv"
              className="max-h-[min(668px,60vh)] min-h-[125px] overflow-y-auto bg-black/30 py-2 [scrollbar-gutter:stable]"
            >
              {text?.trim() ? (
                <SuggestTags
                  query={text}
                  setQuery={(value) => {
                    const query = value.trim()
                    setText(query)
                    startSearch(query, 'search')
                  }}
                  setLoading={() => {}}
                />
              ) : (
                <>
                  <RecentSearches
                    setQuery={(value) => {
                      const query = value.trim()
                      setText(query)
                      startSearch(query, 'recent_search')
                    }}
                  />
                  <PopularTags
                    setQuery={(value) => {
                      const query = value.trim()
                      setText(query)
                      startSearch(query, 'popular_search')
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
